import EncounterPatientListOptimized from "issara-sdk/apis/EncounterPatientListOptimized_core";
import TriageLatest from "issara-sdk/apis/TriageLatest_core";
import TriageList from "issara-sdk/apis/TriageList_core";
import TriageDetail from "issara-sdk/apis/TriageDetail_core";
import PatientDetailView from "issara-sdk/apis/PatientDetailView_apps_REG";
import EncounterList from "issara-sdk/apis/EncounterList_core";
import EncounterDetail from "issara-sdk/apis/EncounterDetail_core";
import MultiAdverseReactionList from "issara-sdk/apis/MultiAdverseReactionList_apps_ADR";
import AdverseReactionList from "issara-sdk/apis/AdverseReactionList_apps_ADR";
import EncounterActionOnly from "issara-sdk/apis/EncounterActionOnly_core";
import OfficialAccountDivisionDetail from "issara-sdk/apis/OfficialAccountDivisionDetail_apps_PRXM";
import ReportPatientQueue from "issara-sdk/apis/ReportPatientQueue_core";
import ClinicalTerm from "issara-sdk/apis/ClinicalTermList_core";
import AIChatFastAPIRequestView from "issara-sdk/apis/AIChatFastAPIRequestViewM";
// serializer

import {
  ActionHandler,
  BaseSeqState,
  BLC_STATUS,
  GenerateActionTypes,
  generatePrefixedActions,
  PickMainState,
} from "react-lib/apps/HISV3/common/CommonInterface";

import moment from "moment";
import { formatDate, formatUTCtoBEString } from "react-lib/utils/dateUtils";
import { fetchURLToBase64 } from "react-lib/utils/printURL";

// PDFS
import FormPatientQueue from "react-lib/apps/MEH/pdfForm/FormPatientQueue";
import FormLMR from "react-lib/apps/MEH/pdfForm/FormLMR";
import PackageOrderList from "issara-sdk/apis/PackageOrderList_core";
import { SelectCustomerByPatientId } from "react-lib/apps/MEH/MEHInterface";
import getPdfMake from "react-lib/appcon/common/pdfMake";

/** ============== Type-Defining Constants =============  */

export const ACTIONS = {
  SEARCH: "SEARCH",
  // TriageLatest
  GET_TRIAGERLATEST: "GET_TRIAGERLATEST",
  SAVE_TRIAGE: "SAVE_TRIAGE",
  SELECTED_PATIENT: "SELECTED_PATIENT",
  SELECTED_INITIAL_MENU: "SELECTED_INITIAL_MENU",
  // Print List
  GET_PRINTLIST: "GET_PRINTLIST",
  // LMR Report
  PRINT_LMR: "PRINT_LMR",
  // Patch Encounter
  PATCH_ENCOUNTER: "PATCH_ENCOUNTER",
  // Print Patient Queue
  PRINT_PATIENT_QUEUE: "PRINT_PATIENT_QUEUE",
  // รายการ video ที่กำลัง gen (ใช้ในโมดัลบนหน้าคิว)
  FETCH_MELAB_VIDEO_LIST: "FETCH_MELAB_VIDEO_LIST",
} as const;

export type MeLabVideoItem = {
  report_id: string;
  thread_id: string;
  report_name: string;
  patient_name: string | null;
  created_at: string;
  generation_status_text: string;
  current_step: string | null;
  current_step_detail: string | null;
  approval_status_text: string;
};

export const MELAB_VIDEO_VISIBLE_STATUS = new Set([
  "generating_script",
  "processing",
  "complete",
]);

const MELAB_VIDEO_MAX_AGE_DAYS = 2;

export const SEQUENCE_NAME = "PatientQueue" as const;
export const MAIN_CARD_NAME = "CardPatientQueue" as const;
const DEFAULT_LIMIT = 20;

/** =============== Types and Interfaces ===============  */
export type State = Partial<{
  selectedPatient?: Record<string, any>;
  selectedEncounter?: Record<string, any>;
  selectedDivision?: Record<string, any>;
  django?: Record<string, any>;
  PatientQueueSequence?: PatientQueueType;
  meBillingActiveTab?: number;
}>;

export type PatientQueueType = Partial<{
  sequenceIndex?: "Action" | "START" | null;
  supplementData?: Record<string, any>[];
  patientQueueData?: PatientQueueDataType[];
  patientQueueFilter?: {
    from_date?: string;
    to_date?: string;
    first_name?: string;
    last_name?: string;
    patient__hn?: number;
    status?: string;
    exclude_canceled?: boolean;
    exclude_finished?: boolean;
  };
  triageData?: TriageDataType;
  currentPage?: number;
  limit?: number;
  totalPage?: number;
}> | null;

export type PatientQueueDataType = Partial<{
  id?: number | string;
  patient_id?: number | string;
  number?: string;
  hn?: string;
  is_appointment?: boolean;
  triage_level?: string;
  patient_name?: string;
  queue_date?: string;
  queue_time?: string;
  status?: string;
  [key: string]: any;
}>;

export type TriageDataType = Partial<{
  triageId?: string;
  chiefComplaint?: string;
  underlyingDisease?: string;
  allergyId?: string;
  allergy?: string;
  regularlyTakenSupplements?: string[];
  preparationBeforeExam?: string;
  preparationRemark?: string;
  regularlyTakenSupplementDataList?: string[];
}> | null;

export type PickedState = PickMainState<
  "buttonLoadCheck" | "errorMessage" | "loadingStatus" | "masterOptions"
>;

export type Event = { message: "GetMasterData"; params: Record<string, any> };

export type Data = { division?: number; device?: number; masterData?: any };

export type SeqState = BaseSeqState<SequenceName>;

export type Actions = typeof ACTIONS;

type SequenceName = typeof SEQUENCE_NAME;

type ActionPayloadMap = {
  [ACTIONS.SEARCH]: {
    currentPage?: number;
    limit?: number;
  };
  [ACTIONS.GET_TRIAGERLATEST]: {
    data: Record<string, any>;
    callback?: (type: string) => void;
  };
  [ACTIONS.SAVE_TRIAGE]: {
    callback?: (type: string) => void;
  };
  [ACTIONS.SELECTED_PATIENT]: {
    data: Record<string, any>;
    type: string;
    callback?: (type: string) => void;
  };
  [ACTIONS.SELECTED_INITIAL_MENU]: {
    data: Record<string, any>;
    initialMenu: string;
  };
  [ACTIONS.GET_PRINTLIST]: {
    data: Record<string, any>;
    type: string;
    callback?: (type: string) => void;
  };
  [ACTIONS.PRINT_LMR]: {
    data: Record<string, any>;
  };
  [ACTIONS.PATCH_ENCOUNTER]: {
    encounterId?: string | number;
    status: string;
    isDischarge?: boolean;
    isCancelDischarge?: boolean;
  };
  [ACTIONS.PRINT_PATIENT_QUEUE]: {};
  [ACTIONS.FETCH_MELAB_VIDEO_LIST]: {
    onSuccess?: (items: MeLabVideoItem[]) => void;
    onError?: (err: any) => void;
  };
};

type ActionTypes = GenerateActionTypes<
  Actions,
  ActionPayloadMap,
  SequenceName,
  PickedState & State
>;

type ActionType = ActionTypes["ActionType"];

export type RunSequence = ActionTypes["RunSequence"];

export type SetProp = ActionTypes["SetProp"];

export type OnEvent = ActionTypes["OnEvent"];

type Params<A extends ActionType["action"]> = ActionTypes["Params"][A];

export type Handler<P = unknown, R = void> = ActionHandler<PickedState & State, Event, Data, P, R>;

export type OptionType = {
  key: string;
  text: string;
  value: string;
};

export type OptionsState = {
  underlyingDisease: OptionType[];
  supplementData: OptionType[];
};

/** ===================== Constants ====================  */
export const StateInitial: State = {
  PatientQueueSequence: null,
};

export const DataInitial: Data = {};

export const BTN_ACTS = generatePrefixedActions(ACTIONS, SEQUENCE_NAME);

export const Masters = [
  ["EncounterStatus", {}],
  ["underlyingDisease", {}],
  ["currentMeds", {}],
  ["province", {}],
  ["district", {}],
  ["city", {}],
] as const;

let toDate = moment();

/** ===================== Functions ====================  */
export const Start: Handler<SeqState> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.SEARCH]: BLC_STATUS.LOADING },
  });

  controller.handleEvent({
    message: "GetMasterData",
    params: { masters: Masters },
  });

  const date = formatDate(toDate);

  const [
    [encounterListRes, encounterListErr, encounterListNet],
    [supplementRes, supplementErr, supplementNet],
  ] = await Promise.all([
    EncounterPatientListOptimized.get({
      apiToken: controller.apiToken,
      params: {
        from_date: date,
        to_date: date,
        exclude_canceled: true,
        exclude_finished: true,
        division: controller.data.division,
        limit: 20,
        offset: 0,
      },
      extra: { division: controller.data.division },
    }),
    ClinicalTerm.list({
      apiToken: controller.apiToken,
      params: {
        type: "supplement",
        division: controller.data.division,
      },
      extra: { division: controller.data.division },
    }),
  ]);

  controller.setState(
    {
      PatientQueueSequence: {
        sequenceIndex: "Action",
        supplementData: supplementRes?.items || [],
        patientQueueData: encounterListRes?.items || [],
        patientQueueFilter: {
          from_date: date,
          to_date: date,
          status: "ALL",
          exclude_canceled: true,
          exclude_finished: true,
        },
        currentPage: 0,
        limit: DEFAULT_LIMIT,
        totalPage: 1,
      },
      meBillingActiveTab: undefined,
    },
    () => Action(controller, { ...params, action: ACTIONS.SEARCH }),
  );

  if (encounterListRes) {
    controller.setState({
      buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.SEARCH]: BLC_STATUS.SUCCESS },
    });
  } else {
    controller.setState({
      buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.SEARCH]: BLC_STATUS.ERROR },
    });
  }
};

/** ====================================================  */
/**                        Action                         */
/** ====================================================  */
export const Action: Handler<ActionType> = async (controller, params) => {
  const actionHandlers: Partial<{ [K in ActionType["action"]]: Handler<Params<K>> }> = {
    [ACTIONS.SEARCH]: HandleSearchPatientQueue,
    [ACTIONS.GET_TRIAGERLATEST]: HandleGetTriageLatest,
    [ACTIONS.SAVE_TRIAGE]: HandleSaveTriage,
    [ACTIONS.SELECTED_PATIENT]: HandleSelectedPatient,
    [ACTIONS.SELECTED_INITIAL_MENU]: HandleSelectedInitialMenu,
    [ACTIONS.GET_PRINTLIST]: HandleGetPrintList,
    [ACTIONS.PRINT_LMR]: HandlePrintLMR,
    [ACTIONS.PATCH_ENCOUNTER]: HandlePatchEncounter,
    [ACTIONS.PRINT_PATIENT_QUEUE]: HandlePrintPatientQueue,
    [ACTIONS.FETCH_MELAB_VIDEO_LIST]: HandleFetchMeLabVideoList,
  };
  const { action } = params;

  const handler = actionHandlers[action] as Handler<Params<typeof action>> | undefined;

  if (handler) {
    handler(controller, params);
  } else {
    console.error(`No handler found for action: ${action}`);
  }
};

/** ====================================================  */
/**                        Handler                        */
/** ====================================================  */
export const HandleSearchPatientQueue: Handler<Params<"SEARCH">> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.SEARCH]: BLC_STATUS.LOADING },
  });

  const patientQueueFilter = state.PatientQueueSequence?.patientQueueFilter;
  const limit = params?.limit ?? state.PatientQueueSequence?.limit ?? DEFAULT_LIMIT;
  const currentPage = params?.currentPage ?? state.PatientQueueSequence?.currentPage ?? 0;

  const [[resp, err], [supplementRes, supplementErr, supplementNet]] = await Promise.all([
    EncounterPatientListOptimized.get({
      apiToken: controller.apiToken,
      params: {
        from_date: patientQueueFilter?.from_date,
        to_date: patientQueueFilter?.to_date,
        first_name: patientQueueFilter?.first_name,
        last_name: patientQueueFilter?.last_name,
        patient__hn: patientQueueFilter?.patient__hn,
        ...(state.PatientQueueSequence?.patientQueueFilter?.status !== "ALL" && {
          status: patientQueueFilter?.status,
        }),
        ...(state.PatientQueueSequence?.patientQueueFilter?.exclude_canceled && {
          exclude_canceled: true,
        }),
        ...(state.PatientQueueSequence?.patientQueueFilter?.exclude_finished && {
          exclude_finished: true,
        }),
        division: controller.data.division,
        limit: limit,
        offset: currentPage * limit,
      },
      extra: { division: controller.data.division },
    }),
    ClinicalTerm.list({
      apiToken: controller.apiToken,
      params: {
        type: "supplement",
        division: controller.data.division,
      },
      extra: { division: controller.data.division },
    }),
  ]);

  if (resp) {
    controller.setState({
      PatientQueueSequence: {
        ...state.PatientQueueSequence,
        patientQueueData: resp?.items || [],
        supplementData: supplementRes?.items || [],
        currentPage: currentPage,
        limit: limit,
        totalPage: Math.ceil(resp?.total / limit),
      },
      buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.SEARCH]: BLC_STATUS.SUCCESS },
    });
  } else {
    controller.setState({
      buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.SEARCH]: BLC_STATUS.ERROR },
    });
  }
};

const HandleGetTriageLatest: Handler<Params<"GET_TRIAGERLATEST">> = async (controller, params) => {
  const state = controller.getState();

  const [
    [triageRes, triageErr, triageNet],
    [patientDetailRes, patientDetailErr, patientDetailNet],
    [encounterDetailRes, encounterDetailErr, encounterDetailNet],
    [adverseReactionRes, adverseReactionErr, adverseReactionNet],
  ] = await Promise.all([
    TriageLatest.retrieve({
      apiToken: controller.apiToken,
      params: { encounter: params.data.id },
      extra: { division: controller.data.division },
    }),
    PatientDetailView.retrieve({
      apiToken: controller.apiToken,
      pk: params.data.patient,
    }),
    EncounterDetail.retrieve({
      apiToken: controller.apiToken,
      pk: params.data.id,
      extra: { division: controller.data.division },
    }),
    AdverseReactionList.list({
      apiToken: controller.apiToken,
      params: {
        patient: params.data.patient,
        limit: 400,
        exclude_cancel: true,
        show_inactive: false,
      },
    }),
  ]);

  const allergy = adverseReactionRes?.items?.[0];

  const regularlyTakenSupplementDataList = (
    encounterDetailRes?.regularly_taken_supplement_data_list || []
  ).map((item) => item.id);

  controller.setState({
    PatientQueueSequence: {
      ...state.PatientQueueSequence,
      triageData: {
        triageId: triageRes?.id || null,
        chiefComplaint: triageRes?.chief_complaint || "",
        underlyingDisease: encounterDetailRes?.underlying_disease || "",
        allergyId: allergy?.id || "",
        allergy: allergy?.name || "",
        regularlyTakenSupplements: encounterDetailRes?.regularly_taken_supplements || [],
        preparationBeforeExam: encounterDetailRes?.preparation_before_exam || "",
        preparationRemark: encounterDetailRes?.preparation_remark || "",
        regularlyTakenSupplementDataList: regularlyTakenSupplementDataList,
      },
    },
    selectedPatient: patientDetailRes,
    selectedEncounter: encounterDetailRes,
  });

  params.callback?.("vitalSign");
};

const HandleSaveTriage: Handler<Params<"SAVE_TRIAGE">> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.SAVE_TRIAGE]: BLC_STATUS.LOADING },
  });

  const triageData = state.PatientQueueSequence?.triageData;

  // Triage
  const isUpdate = state.PatientQueueSequence?.triageData?.triageId !== null;

  const triagePayload = {
    chief_complaint: triageData?.chiefComplaint,
    division: controller.data.division,
    arrive_status: 62,
    case: 54,
  };

  let triageAPI;

  if (isUpdate) {
    triageAPI = TriageDetail.update({
      apiToken: controller.apiToken,
      pk: state.PatientQueueSequence?.triageData?.triageId,
      data: {
        ...triagePayload,
        pk: state.PatientQueueSequence?.triageData?.triageId,
      },
      extra: { division: controller.data.division },
    });
  } else {
    triageAPI = TriageList.create({
      apiToken: controller.apiToken,
      data: {
        ...triagePayload,
        patient_id: state.selectedPatient?.id,
      },
      extra: { division: controller.data.division },
    });
  }

  const [triageRes, triageErr, triageNet] = await triageAPI;

  if (triageErr) {
    controller.setState({
      buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.SAVE_TRIAGE]: BLC_STATUS.ERROR },
    });
  } else {
    const clinicalTerms =
      triageData?.regularlyTakenSupplementDataList
        ?.map((id) => {
          const med = state.PatientQueueSequence?.supplementData?.find((m) => m.id === id);

          if (med) {
            return { id: med.id, name: med.name };
          } else {
            return { name: id };
          }
        })
        .filter(Boolean) ?? [];

    const results = await Promise.all([
      EncounterDetail.update({
        apiToken: controller.apiToken,
        data: {
          chief_complaints: state.selectedEncounter?.chief_complaints,
          location: state.selectedEncounter?.location,
          number: state.selectedEncounter?.number,
          pk: state.selectedEncounter?.pk,
          type: "OPD",
          patient: state.selectedEncounter?.patient,
          division: state.selectedEncounter?.division,
          doctor: state.selectedEncounter?.doctor,
          underlying_disease: triageData?.underlyingDisease,
          regularly_taken_supplements: triageData?.regularlyTakenSupplements || [],
          regularly_taken_supplement_data_list: clinicalTerms || [],
          action: "OPD_EDIT",
          ...(triageData?.preparationBeforeExam && {
            preparation_before_exam: triageData?.preparationBeforeExam,
          }),
          ...(triageData?.preparationRemark && {
            preparation_remark: triageData?.preparationRemark,
          }),
        },
        pk: state.selectedEncounter?.id,
        extra: { division: controller.data.division },
      }),
      MultiAdverseReactionList.create({
        apiToken: controller.apiToken,
        data: {
          action: "NOTE",
          patient: state.selectedPatient?.id,
          items: [
            {
              other_adversary: triageData?.allergy,
              adversary_type: "OTHER",
              type: "OTHER",
              typeCreate: 4,
              note: "-",
              ...(triageData?.allergyId ? { pk: triageData.allergyId } : {}),
            },
          ],
        },
        extra: { division: controller.data.division },
      }),
    ]);

    const [
      [encounterDetailRes, encounterDetailErr, encounterDetailNet],
      [adverseReactionRes, adverseReactionErr, adverseReactionNet],
    ] = results;

    if (encounterDetailErr || adverseReactionErr) {
      controller.setState({
        buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.SAVE_TRIAGE]: BLC_STATUS.ERROR },
      });
    } else {
      if (encounterDetailRes?.status === "ARRIVING") {
        await HandlePatchEncounter(controller, {
          sequence: "PatientQueue",
          action: "PATCH_ENCOUNTER",
          status: "OPD_SCREEN",
        });
      } else {
        await HandleSearchPatientQueue(controller, {
          sequence: "PatientQueue",
          action: "SEARCH",
        });

        await HandleGetTriageLatest(controller, {
          sequence: "PatientQueue",
          action: "GET_TRIAGERLATEST",
          data: encounterDetailRes,
        });
      }

      controller.setState({
        buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.SAVE_TRIAGE]: BLC_STATUS.SUCCESS },
      });

      if (!isUpdate) {
        params.callback?.("printForm");
      }
    }
  }
};

const HandleSelectedPatient: Handler<Params<"SELECTED_PATIENT">> = async (controller, params) => {
  const state = controller.getState();

  const [
    [patientDetailRes, patientDetailErr, patientDetailNet],
    [encounterDetailRes, encounterDetailErr, encounterDetailNet],
    [packageOrderRes, packageOrderErr, packageOrderNet],
  ] = await Promise.all([
    PatientDetailView.retrieve({
      apiToken: controller.apiToken,
      pk: params.data.patient,
    }),
    EncounterDetail.retrieve({
      apiToken: controller.apiToken,
      pk: params.data.id,
      extra: { division: controller.data.division },
    }),
    PackageOrderList.list({
      apiToken: controller.apiToken,
      params: {
        encounter: params.data.id,
      },
      extra: { division: controller.data.division },
    }),
  ]);

  controller.setState({
    selectedPatient: { ...patientDetailRes, package: packageOrderRes?.items },
    selectedEncounter: encounterDetailRes,
  });

  params.callback?.(params.type);
};

const HandleSelectedInitialMenu: Handler<Params<"SELECTED_INITIAL_MENU">> = async (
  controller,
  params,
) => {
  const state = controller.getState();

  const [
    [patientDetailRes, patientDetailErr, patientDetailNet],
    [encounterDetailRes, encounterDetailErr, encounterDetailNet],
    [packageOrderRes, packageOrderErr, packageOrderNet],
  ] = await Promise.all([
    PatientDetailView.retrieve({
      apiToken: controller.apiToken,
      pk: params.data.patient,
    }),
    EncounterDetail.retrieve({
      apiToken: controller.apiToken,
      pk: params.data.id,
    }),
    PackageOrderList.list({
      apiToken: controller.apiToken,
      params: {
        encounter: params.data.id,
      },
      extra: { division: controller.data.division },
    }),
  ]);

  controller.setState({
    selectedPatient: { ...patientDetailRes, package: packageOrderRes?.items },
    selectedEncounter: encounterDetailRes,
  });

  if (params.data.patient && params.initialMenu) {
    SelectCustomerByPatientId(controller, {
      patientId: params.data.patient,
      initialMenu: params.initialMenu,
    });
  }
};

const HandleGetPrintList: Handler<Params<"GET_PRINTLIST">> = async (controller, params) => {
  const state = controller.getState();

  const [
    [patientDetailRes, patientDetailErr, patientDetailNet],
    [encounterDetailRes, encounterDetailErr, encounterDetailNet],
  ] = await Promise.all([
    PatientDetailView.retrieve({
      apiToken: controller.apiToken,
      pk: params.data.patient,
    }),
    EncounterList.list({
      apiToken: controller.apiToken,
      params: {
        patient: params.data.patient,
        unexpired_only: true,
      },
    }),
  ]);

  const selectedEncounter = (encounterDetailRes?.items || [])?.find(
    (item: any) => item?.id === params.data?.id,
  );

  controller.setState({
    selectedPatient: patientDetailRes,
    selectedEncounter: selectedEncounter,
  });

  params.callback?.("printForm");
};

const HandlePrintLMR: Handler<Params<"PRINT_LMR">> = async (controller, params) => {
  const state = controller.getState();

  const [
    [triageRes, triageErr],
    [patientDetailRes, patientDetailErr],
    [encounterDetailRes, encounterDetailErr],
    [adverseReactionRes, adverseReactionErr],
  ] = await Promise.all([
    TriageLatest.retrieve({
      apiToken: controller.apiToken,
      params: { encounter: params.data.id },
      extra: { division: controller.data.division },
    }),
    PatientDetailView.retrieve({
      apiToken: controller.apiToken,
      pk: params.data.patient,
    }),
    EncounterDetail.retrieve({
      apiToken: controller.apiToken,
      pk: params.data.id,
      extra: { division: controller.data.division },
    }),
    AdverseReactionList.list({
      apiToken: controller.apiToken,
      params: {
        patient: params.data.patient,
        limit: 400,
        exclude_cancel: true,
        show_inactive: false,
      },
    }),
  ]);

  const allergy = adverseReactionRes?.items?.[0];

  const currentMeds = (encounterDetailRes?.regularly_taken_supplements ?? [])
    .map((id) => controller.data?.masterData?.currentMeds?.find((m) => m.id === id)?.name)
    .filter(Boolean)
    .join(", ");

  const pdfBytes = await FormLMR({
    triage: triageRes,
    patient: patientDetailRes,
    encounter: encounterDetailRes,
    allergy,
    currentMeds,
  });

  if (!pdfBytes) return;

  const blob = new Blob([pdfBytes], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  window.open(url);
};

export const HandlePatchEncounter: Handler<Params<"PATCH_ENCOUNTER">> = async (
  controller,
  params,
) => {
  const state = controller.getState();

  let [encounterRes, encounterErr, encounterNet] = await EncounterActionOnly.patch({
    pk: params.encounterId || state.selectedEncounter?.id,
    data: {
      action: params.status,
    },
    apiToken: controller.apiToken,
  });

  if (encounterRes && params.isDischarge) {
    [encounterRes, encounterErr, encounterNet] = await EncounterActionOnly.patch({
      pk: params.encounterId || state.selectedEncounter?.id,
      data: {
        action: "OPD_CHECK_OUT",
      },
      apiToken: controller.apiToken,
    });
  }

  if (encounterRes && params.isCancelDischarge) {
    [encounterRes, encounterErr, encounterNet] = await EncounterActionOnly.patch({
      pk: params.encounterId || state.selectedEncounter?.id,
      data: {
        action: "OPD_CANCEL_CHECK_IN",
      },
      apiToken: controller.apiToken,
    });
  }

  if (encounterRes) {
    HandleSearchPatientQueue(controller, {
      sequence: "PatientQueue",
      action: "SEARCH",
    });
  }
};

// ดึงเฉพาะรายการที่ยังทำงานอยู่ ไม่ได้เก็บลง state เพราะโมดัลถือเองและรีเฟรชเมื่อกดเท่านั้น
export const HandleFetchMeLabVideoList: Handler<Params<"FETCH_MELAB_VIDEO_LIST">> = async (
  controller,
  params,
) => {
  const [resp, err] = await AIChatFastAPIRequestView.get<{
    success: boolean;
    message: string;
    data: MeLabVideoItem[];
  }>({
    endpoint: "/medical-ocr/reports/staff/video-explanations",
    params: { limit: 100, offset: 0 },
    apiToken: controller.apiToken,
  });

  if (err || !resp) {
    params.onError?.(err || "ดึงรายการไม่สำเร็จ");

    return;
  }

  const cutoff = moment().subtract(MELAB_VIDEO_MAX_AGE_DAYS, "days");

  const visible = (resp.data || []).filter(
    (item) =>
      MELAB_VIDEO_VISIBLE_STATUS.has(item.generation_status_text) &&
      moment(item.created_at).isAfter(cutoff),
  );

  // lab order เดียวกัน gen ซ้ำได้หลายรอบ — เก็บเฉพาะรอบล่าสุดพอ ไม่งั้นตารางมีของซ้ำ
  const latestByLabOrder = new Map<string, MeLabVideoItem>();

  for (const item of visible) {
    const key = `${item.patient_name ?? ""}|${item.report_name}`;
    const current = latestByLabOrder.get(key);

    if (!current || moment(item.created_at).isAfter(moment(current.created_at))) {
      latestByLabOrder.set(key, item);
    }
  }

  // ตัวที่ยังทำงานอยู่ขึ้นก่อนเสมอ เพราะเป็นสิ่งที่เจ้าหน้าที่เปิดมาดู ที่เหลือเรียงใหม่สุดก่อน
  params.onSuccess?.(
    [...latestByLabOrder.values()].sort((a, b) => {
      const rank = (item: MeLabVideoItem) =>
        item.generation_status_text === "complete" ? 1 : 0;

      return rank(a) - rank(b) || b.created_at.localeCompare(a.created_at);
    }),
  );
};

export const HandlePrintPatientQueue: Handler<Params<"PRINT_PATIENT_QUEUE">> = async (
  controller,
  params,
) => {
  const state = controller.getState();
  const masterData = controller.data.masterData;

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.PRINT_PATIENT_QUEUE]: BLC_STATUS.LOADING,
    },
  });

  const patientQueueFilter = state.PatientQueueSequence?.patientQueueFilter;

  const [resp, err] = await ReportPatientQueue.get({
    apiToken: controller.apiToken,
    params: {
      from_date: patientQueueFilter?.from_date,
      to_date: patientQueueFilter?.to_date,
      first_name: patientQueueFilter?.first_name,
      last_name: patientQueueFilter?.last_name,
      patient__hn: patientQueueFilter?.patient__hn,
      ...(state.PatientQueueSequence?.patientQueueFilter?.status !== "ALL" && {
        status: patientQueueFilter?.status,
      }),
      ...(state.PatientQueueSequence?.patientQueueFilter?.exclude_canceled && {
        exclude_canceled: true,
      }),
      ...(state.PatientQueueSequence?.patientQueueFilter?.exclude_finished && {
        exclude_finished: true,
      }),
      division: controller.data.division,
    },
    extra: { division: controller.data.division },
  });

  if (resp) {
    let oaDetail = {};

    const [oaDetailRes, oaDetailErr, oaDetailNet] = await OfficialAccountDivisionDetail.retrieve({
      params: {},
      extra: { division: controller.data.division || 0 },
      apiToken: controller.apiToken,
    });

    if (oaDetailRes) {
      let imageFile = "";

      if (oaDetailRes?.image_file) {
        imageFile = await fetchURLToBase64(oaDetailRes?.image_file || "");
      }

      const province = (masterData?.province || [])?.find(
        (item) => item.id === oaDetailRes?.accounting_province,
      );
      const district = (masterData?.district || [])?.find(
        (item) => item.id === oaDetailRes?.accounting_district,
      );
      const city = (masterData?.city || [])?.find(
        (item) => item.id === oaDetailRes?.accounting_city,
      );

      oaDetail = {
        name: oaDetailRes?.accounting_business_name || "",
        addressTH: oaDetailRes?.accounting_full_address_text || "",
        addressEN: `${city?.name_en || ""} ${
          district?.name_en || ""
        } ${province?.name_en || ""} ${city?.zipcode || ""}`,
        taxPayerID: oaDetailRes?.accounting_tax_payer_id || "",
        imageFile,
      };
    }

    const data = Object.assign({
      oaDetail,
      startDate: patientQueueFilter?.from_date,
      endDate: patientQueueFilter?.to_date,
      encounterData: resp?.items,
      printedBy: state.django?.user?.full_name || "",
      printDate: formatUTCtoBEString(toDate)?.[0],
      printTime: formatUTCtoBEString(toDate)?.[1],
      division: state.selectedDivision?.name,
      divisionCode: state.selectedDivision?.code,
    });

    let docDef: any = { content: [] };

    docDef = await FormPatientQueue(data);

    const pdfMake = (await getPdfMake(true)).createPdf(docDef);
    pdfMake.open();

    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.PRINT_PATIENT_QUEUE]: BLC_STATUS.SUCCESS,
      },
    });
  } else {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.PRINT_PATIENT_QUEUE]: BLC_STATUS.ERROR,
      },
    });
  }
};
